import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { Task } from '../api/types';
import { formatPredecessorsText, parsePredecessorsText } from '../utils/predecessors';
import { ConnectorStyle, loadConnectorStyle } from '../utils/connectorStyle';
import { toSnapshotItem } from '../utils/taskSnapshot';
import { weekNumber } from '../utils/weekNumber';
import { useAuth } from '../contexts/AuthContext';
import { beginSave, endSave, markSaved } from '../utils/pendingSave';
import { addDaysUTC, computeDepthById, curvedPath, diffDaysUTC, orthogonalPath, parseISODate } from '../utils/ganttGeometry';

const MAX_UNDO_HISTORY = 20;

const DAY_WIDTH = 24;
const ROW_HEIGHT = 28;
const MONTH_ROW_HEIGHT = 18;
const WEEK_ROW_HEIGHT = 16;
const DAY_ROW_HEIGHT = 18;
const HEADER_HEIGHT = MONTH_ROW_HEIGHT + WEEK_ROW_HEIGHT + DAY_ROW_HEIGHT;
const LABEL_WIDTH = 240;
const BAR_HEIGHT = 16;
const HANDLE_HIT_RADIUS = 14;

type Edge = 'start' | 'end';

interface DragLink {
  sourceTaskId: string;
  sourceEdge: Edge;
  originX: number;
  originY: number;
  x: number;
  y: number;
}

/** Combinaison bord-source/bord-cible -> type de dépendance, comme dans un éditeur Gantt classique (voir DHTMLX). */
function dependencyTypeFor(sourceEdge: Edge, targetEdge: Edge): 'FD' | 'FF' | 'DF' | 'DD' {
  if (sourceEdge === 'end' && targetEdge === 'start') return 'FD';
  if (sourceEdge === 'end' && targetEdge === 'end') return 'FF';
  if (sourceEdge === 'start' && targetEdge === 'end') return 'DF';
  return 'DD';
}

interface GanttChartProps {
  diagramId: string;
  canWrite: boolean;
  /** Instantané en lecture seule (une version nommée) — voir DiagramPage. */
  overrideTasks?: Task[];
}

export function GanttChart({ diagramId, canWrite, overrideTasks }: GanttChartProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const weekNumbering = user?.weekNumbering ?? 'ISO';
  const [tasks, setTasks] = useState<Task[]>(() => overrideTasks ?? []);
  const [error, setError] = useState<string | null>(null);
  const [dragLink, setDragLink] = useState<DragLink | null>(null);
  const [showCritical, setShowCritical] = useState(true);
  const [connectorStyle] = useState<ConnectorStyle>(loadConnectorStyle);
  const innerRef = useRef<HTMLDivElement>(null);
  const dragLinkRef = useRef<DragLink | null>(null);
  const historyRef = useRef<Task[][]>([]);
  const [historyLength, setHistoryLength] = useState(0);

  const pushHistory = () => {
    historyRef.current = [...historyRef.current, tasks].slice(-MAX_UNDO_HISTORY);
    setHistoryLength(historyRef.current.length);
  };

  const load = () => {
    if (overrideTasks) return;
    api
      .get<Task[]>(`/diagrams/${diagramId}/tasks`)
      .then(setTasks)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  };

  useEffect(load, [diagramId]);

  const handleUndo = async () => {
    const previous = historyRef.current[historyRef.current.length - 1];
    if (!previous) return;
    historyRef.current = historyRef.current.slice(0, -1);
    setHistoryLength(historyRef.current.length);
    beginSave();
    try {
      await api.put(`/diagrams/${diagramId}/tasks/restore`, { tasks: previous.map(toSnapshotItem) });
      load();
      markSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      endSave();
    }
  };

  const depthById = useMemo(() => computeDepthById(tasks), [tasks]);

  const datedTasks = useMemo(() => tasks.filter((t) => t.startDate && t.endDate), [tasks]);

  const rangeStart = useMemo(() => {
    if (datedTasks.length === 0) {
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      return addDaysUTC(today, -2);
    }
    let min = parseISODate(datedTasks[0].startDate!);
    for (const task of datedTasks) {
      const s = parseISODate(task.startDate!);
      if (s < min) min = s;
    }
    return addDaysUTC(min, -2);
  }, [datedTasks]);

  const totalDays = useMemo(() => {
    if (datedTasks.length === 0) return 30;
    let max = parseISODate(datedTasks[0].endDate!);
    for (const task of datedTasks) {
      const e = parseISODate(task.endDate!);
      if (e > max) max = e;
    }
    return Math.max(diffDaysUTC(rangeStart, max) + 4, 14);
  }, [datedTasks, rangeStart]);

  const xOfDate = (d: Date) => diffDaysUTC(rangeStart, d) * DAY_WIDTH;

  const todayX = useMemo(() => {
    const d = new Date();
    return xOfDate(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart]);
  const showTodayLine = todayX >= 0 && todayX <= totalDays * DAY_WIDTH;

  const dayHeaders = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < totalDays; i++) days.push(addDaysUTC(rangeStart, i));
    return days;
  }, [rangeStart, totalDays]);

  interface HeaderSpan {
    key: string;
    label: string;
    left: number;
    width: number;
  }

  /** Un bloc par mois calendaire couvert par la plage visible, largeur = nombre de jours de ce mois dans la plage. */
  const monthSpans = useMemo<HeaderSpan[]>(() => {
    const spans: HeaderSpan[] = [];
    let startIdx = 0;
    let currentKey = '';
    dayHeaders.forEach((d, i) => {
      const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
      if (key !== currentKey) {
        if (currentKey) {
          spans.push({
            key: currentKey,
            label: dayHeaders[startIdx].toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }),
            left: startIdx * DAY_WIDTH,
            width: (i - startIdx) * DAY_WIDTH,
          });
        }
        currentKey = key;
        startIdx = i;
      }
    });
    if (currentKey) {
      spans.push({
        key: currentKey,
        label: dayHeaders[startIdx].toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }),
        left: startIdx * DAY_WIDTH,
        width: (dayHeaders.length - startIdx) * DAY_WIDTH,
      });
    }
    return spans;
  }, [dayHeaders, i18n.language]);

  /** Un bloc par semaine (ISO 8601 ou américaine selon la préférence utilisateur — voir Paramètres). */
  const weekSpans = useMemo<HeaderSpan[]>(() => {
    const spans: HeaderSpan[] = [];
    let startIdx = 0;
    let currentWeek: number | null = null;
    const weekLabel = (n: number) => t('chart.weekAbbrev', { n });
    dayHeaders.forEach((d, i) => {
      const wk = weekNumber(d, weekNumbering);
      if (wk !== currentWeek) {
        if (currentWeek !== null) {
          spans.push({ key: `${startIdx}`, label: weekLabel(currentWeek), left: startIdx * DAY_WIDTH, width: (i - startIdx) * DAY_WIDTH });
        }
        currentWeek = wk;
        startIdx = i;
      }
    });
    if (currentWeek !== null) {
      spans.push({
        key: `${startIdx}`,
        label: weekLabel(currentWeek),
        left: startIdx * DAY_WIDTH,
        width: (dayHeaders.length - startIdx) * DAY_WIDTH,
      });
    }
    return spans;
  }, [dayHeaders, weekNumbering, t]);

  interface LayoutEntry {
    task: Task;
    y: number;
    isMilestone: boolean;
    hasDates: boolean;
    startX: number;
    endX: number;
    barLeft: number;
    barWidth: number;
  }

  const layout = useMemo<LayoutEntry[]>(() => {
    return tasks.map((task, idx) => {
      const y = idx * ROW_HEIGHT + ROW_HEIGHT / 2;
      if (!task.startDate || !task.endDate) {
        return { task, y, isMilestone: false, hasDates: false, startX: 0, endX: 0, barLeft: 0, barWidth: 0 };
      }
      const start = parseISODate(task.startDate);
      const end = parseISODate(task.endDate);
      const isMilestone = task.startDate.slice(0, 10) === task.endDate.slice(0, 10);
      const barLeft = xOfDate(start);
      const barWidth = (diffDaysUTC(start, end) + 1) * DAY_WIDTH;
      const startX = isMilestone ? barLeft + DAY_WIDTH / 2 - 7 : barLeft;
      const endX = isMilestone ? barLeft + DAY_WIDTH / 2 + 7 : barLeft + barWidth;
      return { task, y, isMilestone, hasDates: true, startX, endX, barLeft, barWidth };
    });
  }, [tasks, rangeStart]);

  const layoutByTaskId = useMemo(() => new Map(layout.map((l) => [l.task.id, l])), [layout]);
  const layoutByNumber = useMemo(() => new Map(layout.map((l) => [l.task.number, l])), [layout]);

  const arrows = useMemo(() => {
    const result: { key: string; d: string; critical: boolean }[] = [];
    for (const entry of layout) {
      if (!entry.hasDates || entry.task.isGroup) continue;
      for (const dep of parsePredecessorsText(entry.task.predecessorsText)) {
        const pred = layoutByNumber.get(dep.number);
        if (!pred || !pred.hasDates) continue;
        let x1: number, x2: number;
        if (dep.type === 'FD') {
          x1 = pred.endX;
          x2 = entry.startX;
        } else if (dep.type === 'FF') {
          x1 = pred.endX;
          x2 = entry.endX;
        } else if (dep.type === 'DF') {
          x1 = pred.startX;
          x2 = entry.endX;
        } else {
          x1 = pred.startX;
          x2 = entry.startX;
        }
        const y1 = pred.y;
        const y2 = entry.y;
        const d = connectorStyle === 'orthogonal' ? orthogonalPath(x1, y1, x2, y2) : curvedPath(x1, y1, x2, y2);
        result.push({
          key: `${pred.task.id}-${entry.task.id}-${dep.type}`,
          d,
          critical: showCritical && pred.task.isCritical && entry.task.isCritical,
        });
      }
    }
    return result;
  }, [layout, layoutByNumber, connectorStyle, showCritical]);

  useEffect(() => {
    dragLinkRef.current = dragLink;
  }, [dragLink]);

  useEffect(() => {
    if (!dragLink) return;

    const toLocal = (clientX: number, clientY: number) => {
      const rect = innerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: clientX - rect.left, y: clientY - rect.top - HEADER_HEIGHT };
    };

    const handleMove = (e: PointerEvent) => {
      const { x, y } = toLocal(e.clientX, e.clientY);
      setDragLink((prev) => (prev ? { ...prev, x, y } : prev));
    };

    const handleUp = async (e: PointerEvent) => {
      const current = dragLinkRef.current;
      setDragLink(null);
      if (!current) return;
      const { x, y } = toLocal(e.clientX, e.clientY);
      let bestTarget: { entry: LayoutEntry; edge: Edge } | null = null;
      let bestDist = HANDLE_HIT_RADIUS;
      for (const entry of layout) {
        if (!entry.hasDates || entry.task.id === current.sourceTaskId) continue;
        for (const edge of ['start', 'end'] as Edge[]) {
          const hx = edge === 'start' ? entry.startX : entry.endX;
          const hy = entry.y;
          const dist = Math.hypot(hx - x, hy - y);
          if (dist < bestDist) {
            bestDist = dist;
            bestTarget = { entry, edge };
          }
        }
      }
      if (!bestTarget) return;
      const sourceEntry = layoutByTaskId.get(current.sourceTaskId);
      if (!sourceEntry) return;
      const type = dependencyTypeFor(current.sourceEdge, bestTarget.edge);
      const targetTask = bestTarget.entry.task;
      const existing = parsePredecessorsText(targetTask.predecessorsText);
      if (existing.some((entry) => entry.number === sourceEntry.task.number && entry.type === type)) return;
      const nextText = formatPredecessorsText([...existing, { number: sourceEntry.task.number, type, lagDays: 0 }]);
      pushHistory();
      beginSave();
      try {
        await api.patch(`/tasks/${targetTask.id}`, { predecessorsText: nextText });
        load();
        markSaved();
      } catch (err) {
        const message = err instanceof ApiError ? err.message : String(err);
        setError(message);
        alert(message);
      } finally {
        endSave();
      }
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragLink !== null, layout, layoutByTaskId]);

  const startLink = (entry: LayoutEntry, edge: Edge, e: React.PointerEvent) => {
    if (!canWrite) return;
    e.preventDefault();
    e.stopPropagation();
    const x = edge === 'start' ? entry.startX : entry.endX;
    const y = entry.y;
    setDragLink({ sourceTaskId: entry.task.id, sourceEdge: edge, originX: x, originY: y, x, y });
  };

  return (
    <div>
      {error && <p className="error-text">{error}</p>}
      <div className="toolbar" style={{ marginBottom: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={showCritical} onChange={(e) => setShowCritical(e.target.checked)} />
          {t('chart.showCriticalPath')}
        </label>
        {canWrite && (
          <button className="btn" disabled={historyLength === 0} onClick={handleUndo} title={t('tasks.undoHint') as string}>
            ↩ {t('tasks.undo')}
          </button>
        )}
      </div>
      <div className="card" style={{ display: 'flex', padding: 0, overflow: 'hidden' }}>
        <div style={{ width: LABEL_WIDTH, flexShrink: 0, borderRight: '1px solid var(--color-border)' }}>
          <div
            style={{
              height: HEADER_HEIGHT,
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 8,
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {t('tasks.title')}
          </div>
          {tasks.map((task) => (
            <div
              key={task.id}
              style={{
                height: ROW_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                paddingLeft: 8 + (depthById.get(task.id) ?? 0) * 12,
                borderBottom: '1px solid var(--color-border)',
                fontSize: 13,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={task.nameFr}
            >
              {showCritical && task.isCritical && (
                <span style={{ color: 'var(--color-danger)' }} title={t('chart.critical') as string}>
                  ●
                </span>
              )}
              {task.isGroup && <span>📁</span>}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {task.number}. {task.nameFr}
              </span>
            </div>
          ))}
        </div>

        <div style={{ overflowX: 'auto', flex: 1 }}>
          <div ref={innerRef} style={{ position: 'relative', width: totalDays * DAY_WIDTH }}>
            <div style={{ height: HEADER_HEIGHT, position: 'relative', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ position: 'relative', height: MONTH_ROW_HEIGHT, borderBottom: '1px solid var(--color-border)' }}>
                {monthSpans.map((span) => (
                  <div
                    key={span.key}
                    style={{
                      position: 'absolute',
                      left: span.left,
                      top: 0,
                      width: span.width,
                      height: MONTH_ROW_HEIGHT,
                      textAlign: 'center',
                      fontSize: 11,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      borderLeft: '1px solid var(--color-border)',
                      textTransform: 'capitalize',
                    }}
                  >
                    {span.label}
                  </div>
                ))}
              </div>
              <div style={{ position: 'relative', height: WEEK_ROW_HEIGHT, borderBottom: '1px solid var(--color-border)' }}>
                {weekSpans.map((span) => (
                  <div
                    key={span.key}
                    title={t('chart.weekTitle') as string}
                    style={{
                      position: 'absolute',
                      left: span.left,
                      top: 0,
                      width: span.width,
                      height: WEEK_ROW_HEIGHT,
                      textAlign: 'center',
                      fontSize: 10,
                      color: 'var(--color-text-muted)',
                      overflow: 'hidden',
                      borderLeft: '1px solid var(--color-border)',
                    }}
                  >
                    {span.label}
                  </div>
                ))}
              </div>
              <div style={{ position: 'relative', height: DAY_ROW_HEIGHT }}>
                {dayHeaders.map((d, i) => (
                  <div
                    key={i}
                    style={{
                      position: 'absolute',
                      left: i * DAY_WIDTH,
                      top: 0,
                      width: DAY_WIDTH,
                      height: DAY_ROW_HEIGHT,
                      textAlign: 'center',
                      fontSize: 10,
                      color: 'var(--color-text-muted)',
                      borderLeft: d.getUTCDay() === 1 ? '1px solid var(--color-border)' : undefined,
                      paddingTop: 2,
                    }}
                  >
                    {d.getUTCDate()}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ position: 'relative', height: tasks.length * ROW_HEIGHT }}>
              {tasks.map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: idx * ROW_HEIGHT,
                    height: ROW_HEIGHT,
                    borderBottom: '1px solid var(--color-border)',
                    background:
                      dayHeaders.length &&
                      idx % 2 === 1
                        ? 'rgba(0,0,0,0.015)'
                        : undefined,
                  }}
                />
              ))}

              {layout.map((entry) => {
                if (!entry.hasDates) return null;
                if (entry.isMilestone) {
                  const cx = entry.barLeft + DAY_WIDTH / 2;
                  const critical = showCritical && entry.task.isCritical;
                  return (
                    <div
                      key={entry.task.id}
                      title={entry.task.nameFr}
                      style={{
                        position: 'absolute',
                        left: cx - 7,
                        top: entry.y - 7,
                        width: 14,
                        height: 14,
                        boxSizing: 'border-box',
                        transform: 'rotate(45deg)',
                        background: entry.task.isGroup ? '#475569' : 'var(--color-primary)',
                        // Le chemin critique ne colore que le BORD, jamais le remplissage (voir demande utilisateur).
                        border: critical ? '2px solid var(--color-danger)' : '2px solid transparent',
                        borderRadius: 2,
                      }}
                    >
                      {canWrite && (
                        <>
                          <div
                            onPointerDown={(e) => startLink(entry, 'start', e)}
                            style={{ position: 'absolute', inset: -6, cursor: 'crosshair' }}
                          />
                        </>
                      )}
                    </div>
                  );
                }
                const critical = showCritical && entry.task.isCritical;
                return (
                  <div
                    key={entry.task.id}
                    title={`${entry.task.nameFr} (${entry.task.progressPct}%)`}
                    style={{
                      position: 'absolute',
                      left: entry.barLeft,
                      top: entry.y - BAR_HEIGHT / 2,
                      width: Math.max(entry.barWidth, 4),
                      height: BAR_HEIGHT,
                      boxSizing: 'border-box',
                      borderRadius: 3,
                      background: entry.task.isGroup ? '#475569' : 'var(--color-primary)',
                      // Le chemin critique ne colore que le BORD, jamais le remplissage (voir demande utilisateur).
                      border: critical ? '2px solid var(--color-danger)' : '2px solid transparent',
                      opacity: entry.task.isGroup ? 0.9 : 1,
                    }}
                  >
                    {!entry.task.isGroup && entry.task.progressPct > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: `${Math.min(entry.task.progressPct, 100)}%`,
                          background: 'rgba(0,0,0,0.25)',
                          borderRadius: '3px 0 0 3px',
                        }}
                      />
                    )}
                    {canWrite && (
                      <>
                        <div
                          onPointerDown={(e) => startLink(entry, 'start', e)}
                          className="gantt-handle"
                          style={{ position: 'absolute', left: -6, top: -2, width: 10, height: 10 }}
                        />
                        <div
                          onPointerDown={(e) => startLink(entry, 'end', e)}
                          className="gantt-handle"
                          style={{ position: 'absolute', right: -6, top: -2, width: 10, height: 10 }}
                        />
                      </>
                    )}
                  </div>
                );
              })}

              <svg
                width={totalDays * DAY_WIDTH}
                height={tasks.length * ROW_HEIGHT}
                style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
              >
                <defs>
                  <marker id="gantt-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill="var(--color-text-muted)" />
                  </marker>
                  <marker id="gantt-arrow-critical" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill="var(--color-danger)" />
                  </marker>
                </defs>
                {arrows.map((arrow) => (
                  <path
                    key={arrow.key}
                    d={arrow.d}
                    fill="none"
                    stroke={arrow.critical ? 'var(--color-danger)' : 'var(--color-text-muted)'}
                    strokeWidth={arrow.critical ? 2 : 1.5}
                    markerEnd={arrow.critical ? 'url(#gantt-arrow-critical)' : 'url(#gantt-arrow)'}
                  />
                ))}
                {dragLink && (
                  <path
                    d={`M ${dragLink.originX},${dragLink.originY} L ${dragLink.x},${dragLink.y}`}
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeWidth={2}
                    strokeDasharray="4 3"
                  />
                )}
              </svg>
            </div>

            {showTodayLine && (
              <div
                title={t('chart.todayLine') as string}
                style={{
                  position: 'absolute',
                  left: todayX,
                  top: 0,
                  width: 1,
                  height: HEADER_HEIGHT + tasks.length * ROW_HEIGHT,
                  background: 'var(--color-danger)',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              />
            )}
          </div>
        </div>
      </div>
      {canWrite && <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 6 }}>{t('chart.linkHint')}</p>}
    </div>
  );
}
